/**
 * Shared cinematic reveal primitives used across DTScotslad pages.
 * - Reveal: fades + rises when in view (Framer Motion).
 * - MaskedLine: overflow-clipped upward "curtain" reveal for headline lines.
 * - HighlandBackdrop: drop-in atmospheric layer (mist + ridges + grain) that
 *   any page can wrap around itself for the cinematic Scotland-highland feel.
 *
 * These are intentionally small — they compose, they do not prescribe layout.
 */
import { useRef } from "react";
import { motion, useInView } from "framer-motion";

export const easeOut = [0.16, 1, 0.3, 1];

export function Reveal({ children, delay = 0, y = 28, className = "", once = true, testid }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once, margin: "-12% 0px -8% 0px" });
    return (
        <motion.div
            ref={ref}
            data-testid={testid}
            initial={{ opacity: 0, y }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.95, delay, ease: easeOut }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

export function MaskedLine({ children, delay = 0, className = "", testid }) {
    return (
        <span
            className="inline-block overflow-hidden align-bottom"
            style={{ lineHeight: 1, paddingRight: "0.08em", paddingBottom: "0.06em" }}
        >
            <motion.span
                data-testid={testid}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.15, delay, ease: easeOut }}
                className={`inline-block ${className}`}
            >
                {children}
            </motion.span>
        </span>
    );
}

/**
 * HighlandBackdrop — one absolutely-positioned layer that renders:
 *   • a faint radial mist (bottom)
 *   • a low SVG ridge silhouette
 *   • a whisper of grain
 * It self-contains z-index and pointer-events. Wrap it inside a
 * `relative` container and the rest of the page content will sit above.
 */
export function HighlandBackdrop({ variant = "default" }) {
    // Variants slightly change ridge height + mist intensity so pages don't
    // all feel identical. Kept subtle — never louder than the content.
    const map = {
        default: { ridge: "38vh", opacity: 0.32 },
        soft:    { ridge: "28vh", opacity: 0.22 },
        deep:    { ridge: "48vh", opacity: 0.42 },
    };
    const cfg = map[variant] || map.default;

    return (
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="highland-backdrop">
            {/* Rising mist */}
            <div
                className="absolute inset-x-0 bottom-0"
                style={{
                    height: "60%",
                    background: `radial-gradient(120% 55% at 50% 100%, rgba(107,167,222,0.10) 0%, rgba(107,167,222,0) 55%), linear-gradient(to top, rgba(11,31,58,0.55) 0%, rgba(11,31,58,0) 90%)`,
                }}
            />
            {/* Ridge silhouette */}
            <div
                className="absolute inset-x-0 bottom-0"
                style={{
                    height: cfg.ridge,
                    opacity: cfg.opacity,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "100% 100%",
                    backgroundPosition: "bottom center",
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 500' preserveAspectRatio='none'><path d='M0 420 L120 360 L260 400 L400 320 L560 380 L720 300 L880 360 L1040 280 L1200 340 L1360 260 L1500 320 L1600 300 L1600 500 L0 500 Z' fill='%230b1f3a' opacity='0.85'/><path d='M0 460 L160 400 L320 440 L480 380 L640 420 L800 360 L960 410 L1120 340 L1280 400 L1440 340 L1600 380 L1600 500 L0 500 Z' fill='%23142a4d' opacity='0.85'/></svg>\")",
                }}
            />
            {/* Grain wash */}
            <div
                className="absolute inset-0 mix-blend-overlay"
                style={{
                    opacity: 0.06,
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='200' height='200' filter='url(%23n)' opacity='0.5'/></svg>\")",
                }}
            />
        </div>
    );
}

/**
 * splitTitleForDrama(title) — turns a single-line hero title into two
 * cinematic lines: a plain first line + brand-italic second.
 *   • "Scotland, seen from the wind."  →  ["Scotland,",     "seen from the wind."]
 *   • "The Man Behind the Lens"        →  ["The Man Behind","the Lens"]           (last word italic)
 *   • "Say hello."                     →  [null,            "Say hello."]         (whole thing italic)
 * Never mutates the trailing punctuation.
 */
export function splitTitleForDrama(title = "") {
    const raw = String(title).trim();
    if (!raw) return { first: null, second: "" };
    const idx = raw.lastIndexOf(",");
    if (idx !== -1 && idx < raw.length - 1) {
        return { first: raw.slice(0, idx + 1), second: raw.slice(idx + 1).trim() };
    }
    // No comma — split on the last space so the last word gets the italic accent.
    // Very short titles (1-2 words) render whole in italic for maximum drama.
    const words = raw.split(/\s+/);
    if (words.length <= 2) return { first: null, second: raw };
    const lastWord = words.pop();
    return { first: words.join(" "), second: lastWord };
}

/**
 * CinematicTitle — drop-in <h1> with masked-line reveal.
 * Wraps splitTitleForDrama so every page gets the same treatment automatically.
 */
export function CinematicTitle({ text, testid, size = "clamp(2.8rem, 7vw, 7rem)", className = "" }) {
    const { first, second } = splitTitleForDrama(text);
    return (
        <h1
            className={`font-serif font-light tracking-tighter leading-[0.9] ${className}`}
            style={{ fontSize: size }}
            data-testid={testid}
        >
            {first && <div><MaskedLine delay={0.1}>{first}</MaskedLine></div>}
            <div>
                <MaskedLine delay={first ? 0.3 : 0.1}>
                    <span className="italic text-[var(--sb-brand)]">{second}</span>
                </MaskedLine>
            </div>
        </h1>
    );
}

/**
 * PageHeader — reusable dramatic top block for interior pages that don't
 * warrant a full-bleed hero (Blog, Gallery, Things, Map, Contact, etc.).
 * Uses MaskedLine for a curtain-reveal headline and Reveal for the copy.
 *
 * Props:
 *   overline: string
 *   titleLines: [string, string?]  — second line renders in italic brand
 *   lede: string
 *   align: "left" | "split"        — split = two-column overline + copy
 *   right: ReactNode               — optional right-side content
 *   testid: string                 — data-testid for the wrapper
 */
export function PageHeader({ overline, titleLines = [], lede, align = "split", right, testid }) {
    const [a, b] = titleLines;
    return (
        <header className={`relative ${align === "split" ? "grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12" : ""} mb-14 lg:mb-20`} data-testid={testid}>
            <div className={align === "split" ? "lg:col-span-8" : "max-w-4xl"}>
                <Reveal>
                    <div className="overline mb-4"><span className="editorial-rule" />{overline}</div>
                </Reveal>
                <h1
                    className="font-serif font-light tracking-tighter leading-[0.92]"
                    style={{ fontSize: "clamp(2.6rem, 7vw, 6.5rem)" }}
                    data-testid={testid ? `${testid}-title` : undefined}
                >
                    <div><MaskedLine delay={0.05}>{a}</MaskedLine></div>
                    {b && (
                        <div>
                            <MaskedLine delay={0.2}>
                                <span className="italic text-[var(--sb-brand)]">{b}</span>
                            </MaskedLine>
                        </div>
                    )}
                </h1>
            </div>
            {(lede || right) && (
                <div className={align === "split" ? "lg:col-span-4 self-end" : "mt-8 max-w-2xl"}>
                    <Reveal delay={0.25}>
                        {right ? right : (
                            <p className="text-[var(--sb-text-dim)] text-lg leading-relaxed">{lede}</p>
                        )}
                    </Reveal>
                </div>
            )}
        </header>
    );
}
